import DateRange from "shared/helpers/DateRange";
import { DOUBLE_WIDTH_WHEN_MANY_STREAMS_CHARTS } from "shared/modules/Charts/constants";
import {
  ChartAxisInterval,
  ChartVariant,
  type SavedChartSetting,
} from "shared/modules/Charts/types";
import { isChartSettingWithStreamsAndMediaTypes } from "shared/modules/Charts/types/guards";

export const MAX_SINGLE_WIDTH_BUCKETS = 32;
export const MAX_SINGLE_WIDTH_DAYS = Math.ceil(MAX_SINGLE_WIDTH_BUCKETS / 24);
export const MAX_SINGLE_WIDTH_STREAMS = 6;
export const MAX_SINGLE_WIDTH_SHARE_OF_VOICE_STREAMS = 12;

export function useInsightsChartWidth() {
  function isChartRangeDoubleWidth({
    interval,
    range,
  }: {
    interval?: ChartAxisInterval;
    range: DateRange;
  }): boolean {
    const chartInterval: ChartAxisInterval = interval || ChartAxisInterval.Day;

    const { days, hours, months, quarters, weeks, years } = new DateRange(range)
      .spanIntervals;

    switch (chartInterval) {
      case ChartAxisInterval.Week:
        return weeks > MAX_SINGLE_WIDTH_BUCKETS;
      case ChartAxisInterval.Month:
        return months > MAX_SINGLE_WIDTH_BUCKETS;
      case ChartAxisInterval.Quarter:
        return quarters > MAX_SINGLE_WIDTH_BUCKETS;
      case ChartAxisInterval.Year:
        return years > MAX_SINGLE_WIDTH_BUCKETS;
      case ChartAxisInterval.Day:
      default:
        return (
          (hours > MAX_SINGLE_WIDTH_BUCKETS && days <= MAX_SINGLE_WIDTH_DAYS) ||
          days > MAX_SINGLE_WIDTH_BUCKETS
        );
    }
  }

  function isChartStreamsDoubleWidth(setting: SavedChartSetting): boolean {
    if (
      isChartSettingWithStreamsAndMediaTypes(setting) &&
      DOUBLE_WIDTH_WHEN_MANY_STREAMS_CHARTS.includes(setting.chart) &&
      setting.chart_variant !== ChartVariant.Table
    ) {
      const maxSingleWidthStreams =
        setting.chart === "share_of_voice"
          ? MAX_SINGLE_WIDTH_SHARE_OF_VOICE_STREAMS
          : MAX_SINGLE_WIDTH_STREAMS;

      return setting.stream_ids?.length > maxSingleWidthStreams;
    }

    return false;
  }

  function isChartDoubleWidthEnabled(setting: SavedChartSetting): boolean {
    const chartRangeDoubleWidth = isChartRangeDoubleWidth({
      interval: setting.interval,
      range: setting.range,
    });

    const chartStreamsDoubleWidth = isChartStreamsDoubleWidth(setting);

    return chartRangeDoubleWidth || chartStreamsDoubleWidth;
  }

  return {
    isChartDoubleWidthEnabled,
    isChartRangeDoubleWidth,
    isChartStreamsDoubleWidth,
  };
}

export default useInsightsChartWidth;
